import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Plus, Save, Trash2, RotateCcw, CheckCircle2, UserPlus, Users, Scale, Image as ImageIcon, Languages } from 'lucide-react';
import { toast } from 'sonner';
import { editingAdminApi, adminApi } from '@/lib/api';
import { PanelHeader } from './_shared';

const STATUS_COLORS = {
  awaiting_payment: 'bg-amber-100 text-amber-900 border-amber-300',
  paid:             'bg-blue-100 text-blue-900 border-blue-300',
  in_review:        'bg-violet-100 text-violet-900 border-violet-300',
  completed:        'bg-emerald-100 text-emerald-900 border-emerald-300',
  published:        'bg-emerald-200 text-emerald-950 border-emerald-400',
  cancelled:        'bg-zinc-200 text-zinc-700 border-zinc-300',
};

const STATUS_LABELS = {
  awaiting_payment: 'Awaiting payment',
  paid:             'Paid · ready to assign',
  in_review:        'In review',
  completed:        'Completed',
  published:        'Published',
  cancelled:        'Cancelled',
};

function newTier() {
  const sid = `tier_${Math.random().toString(36).slice(2, 8)}`;
  return { id: sid, name: 'New tier', max_words: 50000, tokens: 1000000, desc: '' };
}

function TiersEditor({ tiers, setTiers, onSave, busy }) {
  const update = (i, patch) => setTiers(tiers.map((t, idx) => idx === i ? { ...t, ...patch } : t));
  const remove = (i) => setTiers(tiers.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3" data-testid="editing-tiers-editor">
      <div className="rounded-md border border-border bg-card p-4 shadow-panel">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[13px] font-semibold flex items-center gap-1.5"><Scale className="h-4 w-4" /> Editing pricing tiers</div>
            <p className="mt-1 text-[11px] text-muted-foreground max-w-xl">
              Authors are auto-quoted the smallest tier whose <span className="font-mono-stat">max_words</span> covers their manuscript. The matched tier&apos;s token cost is deducted from the author&apos;s wallet on payment.
            </p>
          </div>
          <Button size="sm" variant="outline" className="gap-1.5"
                  data-testid="editing-add-tier-btn"
                  onClick={() => setTiers([...tiers, newTier()])}>
            <Plus className="h-3.5 w-3.5" /> Add tier
          </Button>
        </div>
        <div className="mt-3 space-y-2">
          {tiers.length === 0 && (
            <div className="rounded-sm border border-dashed border-border p-4 text-center text-[11.5px] text-muted-foreground">
              No tiers configured. Authors cannot request editing until at least one tier is saved.
            </div>
          )}
          {tiers.map((t, i) => (
            <div key={t.id || i} className="grid grid-cols-12 gap-2 rounded-sm border border-border bg-background p-2"
                 data-testid={`editing-tier-row-${i}`}>
              <div className="col-span-3">
                <Label className="text-[10.5px]">Name</Label>
                <Input className="h-8 text-[12px]" value={t.name || ''}
                       data-testid={`editing-tier-name-${i}`}
                       onChange={e => update(i, { name: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label className="text-[10.5px]">Max words</Label>
                <Input className="h-8 text-[12px] font-mono-stat" type="number"
                       data-testid={`editing-tier-maxwords-${i}`}
                       value={t.max_words || 0}
                       onChange={e => update(i, { max_words: parseInt(e.target.value || '0', 10) })} />
              </div>
              <div className="col-span-2">
                <Label className="text-[10.5px]">Tokens charged</Label>
                <Input className="h-8 text-[12px] font-mono-stat" type="number"
                       data-testid={`editing-tier-tokens-${i}`}
                       value={t.tokens || 0}
                       onChange={e => update(i, { tokens: parseInt(e.target.value || '0', 10) })} />
              </div>
              <div className="col-span-4">
                <Label className="text-[10.5px]">Description (shown to author)</Label>
                <Input className="h-8 text-[12px]" value={t.desc || ''}
                       data-testid={`editing-tier-desc-${i}`}
                       onChange={e => update(i, { desc: e.target.value })} />
              </div>
              <div className="col-span-1 flex items-end">
                <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive"
                        data-testid={`editing-tier-remove-${i}`}
                        onClick={() => remove(i)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center justify-end">
          <Button size="sm" onClick={onSave} disabled={busy} className="gap-1.5"
                  data-testid="editing-tiers-save-btn">
            <Save className="h-3.5 w-3.5" /> {busy ? 'Saving…' : 'Save tiers'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function RequestRow({ req, editors, onAssign, onComplete }) {
  const [pick, setPick] = useState(req.assigned_editor_id || '');
  const statusClass = STATUS_COLORS[req.status] || 'bg-zinc-100 text-zinc-700 border-zinc-300';
  const canAssign = ['paid', 'in_review'].includes(req.status);
  const canComplete = ['paid', 'in_review'].includes(req.status);
  return (
    <div className="rounded-md border border-border bg-card p-3 shadow-panel"
         data-testid={`editing-request-${req.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold">{req.project_name || '(unnamed project)'}</div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            <span className="font-mono-stat">{req.id}</span>
            <span className="mx-1.5">·</span>
            Author: <b className="text-foreground">{req.author_name || req.author_email}</b>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px]">
            <Badge className={`border ${statusClass}`}>{STATUS_LABELS[req.status] || req.status}</Badge>
            <span className="text-muted-foreground">Tier:</span>
            <span className="font-mono-stat">{req.tier_name}</span>
            <span className="text-muted-foreground">·</span>
            <span className="font-mono-stat">{(req.word_count || 0).toLocaleString()} words</span>
            <span className="text-muted-foreground">·</span>
            <span className="font-mono-stat">{(req.tokens_quoted || 0).toLocaleString()} tokens</span>
          </div>
          {req.author_notes && (
            <p className="mt-1.5 rounded-sm border border-border bg-panel-header/40 px-2 py-1 text-[11px] text-muted-foreground">
              {req.author_notes}
            </p>
          )}
        </div>
        <div className="flex w-full max-w-md flex-col gap-2 sm:w-auto">
          {canAssign && (
            <div className="flex items-center gap-1.5">
              <select className="h-8 flex-1 rounded-sm border border-border bg-background px-2 text-[12px]"
                      value={pick} onChange={e => setPick(e.target.value)}
                      data-testid={`editing-assign-select-${req.id}`}>
                <option value="">Select editor…</option>
                {editors.map(e => (
                  <option key={e.user_id} value={e.user_id}>{e.name || e.email}</option>
                ))}
              </select>
              <Button size="sm" disabled={!pick} className="h-8 gap-1.5"
                      data-testid={`editing-assign-btn-${req.id}`}
                      onClick={() => onAssign(req.id, pick)}>
                <UserPlus className="h-3.5 w-3.5" /> Assign
              </Button>
            </div>
          )}
          {req.assigned_editor_id && (
            <div className="text-[11px] text-muted-foreground">
              Assigned to <b className="text-foreground">{req.editor_name || req.editor_email}</b>
            </div>
          )}
          {canComplete && (
            <Button size="sm" variant="outline" className="h-8 gap-1.5"
                    data-testid={`editing-complete-btn-${req.id}`}
                    onClick={() => onComplete(req.id)}>
              <CheckCircle2 className="h-3.5 w-3.5" /> Mark completed
            </Button>
          )}
          {req.status === 'completed' && (
            <div className="rounded-sm border border-emerald-300 bg-emerald-50 px-2 py-1 text-[11px] text-emerald-900">
              Author can now publish to the bookstore.
            </div>
          )}
          {req.status === 'published' && req.bookstore_book_id && (
            <div className="text-[11px] text-muted-foreground">
              Book: <span className="font-mono-stat">{req.bookstore_book_id}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function EditorsManager() {
  const [users, setUsers] = useState([]);
  const [editors, setEditors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('');

  const load = async () => {
    setBusy(true);
    try {
      const [u, e] = await Promise.all([adminApi.users(), editingAdminApi.listEditors()]);
      setUsers(u.users || u || []);
      setEditors(e.editors || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load users.');
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const editorIds = useMemo(() => new Set(editors.map(e => e.user_id)), [editors]);
  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u => (u.email || '').toLowerCase().includes(q) || (u.name || '').toLowerCase().includes(q));
  }, [users, filter]);

  const toggle = async (u, makeEditor) => {
    try {
      await editingAdminApi.toggleEditorRole(u.user_id, makeEditor);
      toast.success(makeEditor ? `${u.email} is now an editor.` : `${u.email} demoted.`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update role.');
    }
  };

  return (
    <div className="rounded-md border border-border bg-card p-4 shadow-panel">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-[13px] font-semibold flex items-center gap-1.5"><Users className="h-4 w-4" /> Editor pool</div>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Promote a user to the <span className="font-mono-stat">editor</span> role so they can be assigned editing jobs. Editors see only their assigned manuscripts.
          </p>
        </div>
        <Input className="h-8 w-64 text-[12px]" placeholder="Filter by email/name…"
               data-testid="editing-editor-filter"
               value={filter} onChange={e => setFilter(e.target.value)} />
      </div>
      <div className="mt-3 max-h-[480px] overflow-auto rounded-sm border border-border">
        <table className="w-full text-[12px]">
          <thead className="sticky top-0 bg-panel-header/60">
            <tr className="text-left text-[11px] text-muted-foreground">
              <th className="px-2 py-1.5">Email</th>
              <th className="px-2 py-1.5">Name</th>
              <th className="px-2 py-1.5">Role</th>
              <th className="px-2 py-1.5 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(u => {
              const isEd = editorIds.has(u.user_id);
              return (
                <tr key={u.user_id} className="border-t border-border" data-testid={`editing-user-row-${u.user_id}`}>
                  <td className="px-2 py-1.5 font-mono-stat">{u.email}</td>
                  <td className="px-2 py-1.5">{u.name || '—'}</td>
                  <td className="px-2 py-1.5">
                    {isEd ? <Badge className="border border-violet-300 bg-violet-100 text-violet-900">editor</Badge>
                          : <span className="text-muted-foreground">{u.role || 'ordinary'}</span>}
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <Button size="sm" variant={isEd ? 'outline' : 'default'} disabled={busy}
                            data-testid={`editing-toggle-editor-${u.user_id}`}
                            onClick={() => toggle(u, !isEd)}>
                      {isEd ? 'Demote' : 'Make editor'}
                    </Button>
                  </td>
                </tr>
              );
            })}
            {!visible.length && (
              <tr><td colSpan={4} className="px-2 py-4 text-center text-muted-foreground">No users.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CoverArtTiersEditor() {
  const [tiers, setTiers] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const r = await editingAdminApi.getCoverTiers();
      setTiers(r.tiers || []);
      setDefaults(r.defaults || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load cover tiers.');
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const update = (i, patch) => setTiers(tiers.map((t, idx) => idx === i ? { ...t, ...patch } : t));
  const remove = (i) => setTiers(tiers.filter((_, idx) => idx !== i));

  const save = async () => {
    setBusy(true);
    try {
      const r = await editingAdminApi.saveCoverTiers(tiers);
      setTiers(r.tiers || []);
      toast.success('Cover-art tiers saved.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save cover tiers.');
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-border bg-card p-4 shadow-panel" data-testid="cover-tiers-editor">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[13px] font-semibold flex items-center gap-1.5"><ImageIcon className="h-4 w-4" /> Cover-art tiers</div>
          <p className="mt-1 text-[11px] text-muted-foreground max-w-xl">
            <b>Basic</b> ships free with editing (AI cover via Nano Banana). <b>Pro</b> charges USD at publish time. <b>Custom</b> requires an advance Stripe payment from the author before they can publish.
          </p>
        </div>
        <Button size="sm" variant="outline" className="gap-1.5"
                data-testid="cover-tier-add-btn"
                onClick={() => setTiers([...tiers, { id: `tier_${Math.random().toString(36).slice(2,7)}`, name: 'New', price_usd: 0, kind: 'ai_standard', desc: '' }])}>
          <Plus className="h-3.5 w-3.5" /> Add tier
        </Button>
      </div>
      <div className="mt-3 space-y-2">
        {tiers.map((t, i) => (
          <div key={t.id || `tier-${i}`} className="grid grid-cols-12 gap-2 rounded-sm border border-border bg-background p-2"
               data-testid={`cover-tier-row-${i}`}>
            <div className="col-span-2">
              <Label className="text-[10.5px]">ID</Label>
              <Input className="h-8 text-[12px] font-mono-stat" value={t.id || ''} disabled={t.id === 'custom' || t.id === 'basic'}
                     onChange={e => update(i, { id: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label className="text-[10.5px]">Name</Label>
              <Input className="h-8 text-[12px]" value={t.name || ''} onChange={e => update(i, { name: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label className="text-[10.5px]">Price USD</Label>
              <Input className="h-8 text-[12px] font-mono-stat" type="number" step="0.01" min="0"
                     data-testid={`cover-tier-price-${i}`}
                     value={t.price_usd || 0} onChange={e => update(i, { price_usd: parseFloat(e.target.value || '0') })} />
            </div>
            <div className="col-span-2">
              <Label className="text-[10.5px]">Kind</Label>
              <select className="h-8 w-full rounded-sm border border-border bg-background px-1 text-[12px]"
                      value={t.kind || 'ai_standard'} onChange={e => update(i, { kind: e.target.value })}>
                <option value="ai_standard">AI standard</option>
                <option value="ai_premium">AI premium</option>
                <option value="human_brief">Human brief</option>
              </select>
            </div>
            <div className="col-span-3">
              <Label className="text-[10.5px]">Description</Label>
              <Input className="h-8 text-[12px]" value={t.desc || ''} onChange={e => update(i, { desc: e.target.value })} />
            </div>
            <div className="col-span-1 flex items-end">
              <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive"
                      disabled={t.id === 'custom'}
                      onClick={() => remove(i)}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-end gap-2">
        {defaults?.length > 0 && (
          <Button size="sm" variant="ghost" className="gap-1.5"
                  onClick={() => setTiers(defaults.map(d => ({ ...d })))}>
            <RotateCcw className="h-3.5 w-3.5" /> Reset defaults
          </Button>
        )}
        <Button size="sm" disabled={busy} onClick={save} className="gap-1.5"
                data-testid="cover-tiers-save-btn">
          <Save className="h-3.5 w-3.5" /> Save cover tiers
        </Button>
      </div>
    </div>
  );
}

function LanguageMarkupsEditor() {
  const [rows, setRows] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const r = await editingAdminApi.getLanguageMarkups();
      setRows(r.languages || []);
      setDefaults(r.defaults || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load language markups.');
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const update = (i, patch) => setRows(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const remove = (i) => setRows(rows.filter((_, idx) => idx !== i));

  const save = async () => {
    setBusy(true);
    try {
      const r = await editingAdminApi.saveLanguageMarkups(rows);
      setRows(r.languages || []);
      toast.success('Language markups saved.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save language markups.');
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-border bg-card p-4 shadow-panel" data-testid="lang-markups-editor">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[13px] font-semibold flex items-center gap-1.5"><Languages className="h-4 w-4" /> Bookstore language markups</div>
          <p className="mt-1 text-[11px] text-muted-foreground max-w-xl">
            Buyers in the bookstore can select a target language; the book&apos;s retail price is multiplied by this markup percentage. The translation is generated by Claude on first purchase and cached for subsequent buyers.
          </p>
        </div>
        <Button size="sm" variant="outline" className="gap-1.5"
                data-testid="lang-markup-add-btn"
                onClick={() => setRows([...rows, { code: '', name: '', markup_pct: 0 }])}>
          <Plus className="h-3.5 w-3.5" /> Add language
        </Button>
      </div>
      <div className="mt-3 space-y-2">
        {rows.map((r, i) => (
          <div key={r.code || `row-${i}`} className="grid grid-cols-12 gap-2 rounded-sm border border-border bg-background p-2"
               data-testid={`lang-markup-row-${i}`}>
            <div className="col-span-2">
              <Label className="text-[10.5px]">Code</Label>
              <Input className="h-8 text-[12px] font-mono-stat" value={r.code || ''}
                     onChange={e => update(i, { code: e.target.value.toLowerCase() })} />
            </div>
            <div className="col-span-6">
              <Label className="text-[10.5px]">Name</Label>
              <Input className="h-8 text-[12px]" value={r.name || ''} onChange={e => update(i, { name: e.target.value })} />
            </div>
            <div className="col-span-3">
              <Label className="text-[10.5px]">Markup %</Label>
              <Input className="h-8 text-[12px] font-mono-stat" type="number"
                     data-testid={`lang-markup-pct-${i}`}
                     value={r.markup_pct || 0} onChange={e => update(i, { markup_pct: parseInt(e.target.value || '0', 10) })} />
            </div>
            <div className="col-span-1 flex items-end">
              <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive"
                      onClick={() => remove(i)}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-end gap-2">
        {defaults?.length > 0 && (
          <Button size="sm" variant="ghost" className="gap-1.5"
                  onClick={() => setRows(defaults.map(d => ({ ...d })))}>
            <RotateCcw className="h-3.5 w-3.5" /> Reset defaults
          </Button>
        )}
        <Button size="sm" disabled={busy} onClick={save} className="gap-1.5"
                data-testid="lang-markups-save-btn">
          <Save className="h-3.5 w-3.5" /> Save language markups
        </Button>
      </div>
    </div>
  );
}

export default function EditingPanel() {
  const [tiers, setTiers] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [requests, setRequests] = useState([]);
  const [editors, setEditors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  const load = async () => {
    setBusy(true);
    try {
      const [t, r, e] = await Promise.all([
        editingAdminApi.getTiers(),
        editingAdminApi.listRequests(statusFilter || null),
        editingAdminApi.listEditors(),
      ]);
      setTiers(t.tiers || []);
      setDefaults(t.defaults || []);
      setRequests(r.requests || []);
      setEditors(e.editors || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load editing data.');
    } finally { setBusy(false); }
  };
   
  // iter55f58c — `load` is a stable arrow; only react to statusFilter.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [statusFilter]);

  const saveTiers = async () => {
    setBusy(true);
    try {
      const r = await editingAdminApi.saveTiers(tiers);
      setTiers(r.tiers || []);
      toast.success('Editing tiers saved.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save tiers.');
    } finally { setBusy(false); }
  };

  const assign = async (rid, editorUserId) => {
    try {
      await editingAdminApi.assign(rid, editorUserId);
      toast.success('Editor assigned.');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to assign editor.');
    }
  };

  const complete = async (rid) => {
    try {
      await editingAdminApi.complete(rid);
      toast.success('Request marked completed.');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to complete.');
    }
  };

  return (
    <div className="space-y-4">
      <PanelHeader
        title="Editing & Proofreading"
        subtitle="Configure pricing tiers, manage your editor pool, and assign incoming editing requests. Authors cannot publish manuscripts to the bookstore until their editing request is marked completed."
        onRefresh={load}
      />

      <Tabs defaultValue="requests" className="w-full">
        <TabsList>
          <TabsTrigger value="requests"  data-testid="editing-tab-requests">Requests</TabsTrigger>
          <TabsTrigger value="tiers"     data-testid="editing-tab-tiers">Pricing tiers</TabsTrigger>
          <TabsTrigger value="editors"   data-testid="editing-tab-editors">Editor pool</TabsTrigger>
          <TabsTrigger value="cover-art" data-testid="editing-tab-cover-art">Cover art</TabsTrigger>
          <TabsTrigger value="languages" data-testid="editing-tab-languages">Languages</TabsTrigger>
        </TabsList>

        <TabsContent value="requests" className="mt-3 space-y-3">
          <div className="flex items-center gap-2">
            <Label className="text-[11px]">Filter by status</Label>
            <select className="h-8 rounded-sm border border-border bg-background px-2 text-[12px]"
                    data-testid="editing-status-filter"
                    value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">All</option>
              <option value="awaiting_payment">Awaiting payment</option>
              <option value="paid">Paid</option>
              <option value="in_review">In review</option>
              <option value="completed">Completed</option>
              <option value="published">Published</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          {requests.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-8 text-center text-[12px] text-muted-foreground">
              No editing requests yet.
            </div>
          ) : (
            <div className="space-y-2" data-testid="editing-requests-list">
              {requests.map(r => (
                <RequestRow key={r.id} req={r} editors={editors}
                            onAssign={assign} onComplete={complete} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="tiers" className="mt-3">
          <TiersEditor tiers={tiers} setTiers={setTiers} onSave={saveTiers} busy={busy} />
          {defaults?.length > 0 && (
            <div className="mt-3 flex items-center justify-end">
              <Button size="sm" variant="ghost" className="gap-1.5"
                      data-testid="editing-tiers-reset-btn"
                      onClick={() => setTiers(defaults.map(d => ({ ...d })))}>
                <RotateCcw className="h-3.5 w-3.5" /> Reset to defaults
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="editors" className="mt-3">
          <EditorsManager />
        </TabsContent>

        <TabsContent value="cover-art" className="mt-3">
          <CoverArtTiersEditor />
        </TabsContent>

        <TabsContent value="languages" className="mt-3">
          <LanguageMarkupsEditor />
        </TabsContent>
      </Tabs>
    </div>
  );
}
